// Recuperar senha (`spec/17-frontend.md` §8.1). A API devolve 200 sempre, inclusive
// quando o email não existe: a tela não pode virar oráculo de enumeração.

import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { CAPTCHA_ERROR_MESSAGE, isCaptchaError, requestPasswordReset } from '../api/auth.ts';
import { errorMessageOf } from '../api/errors.ts';
import { AuthShell, FormError } from '../components/app/auth-shell.tsx';
import { TURNSTILE_ENABLED, Turnstile } from '../components/app/turnstile.tsx';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';

const schema = z.object({ email: z.email('Informe um email válido') });

function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  // Token de uso único (SEC-01): remontar o widget pela `key` é o reset.
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);
  const [captchaAttempt, setCaptchaAttempt] = useState(0);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    setError(undefined);
    try {
      await requestPasswordReset(
        { email: values.email, redirectTo: `${window.location.origin}/reset-password` },
        captchaToken,
      );
      setSent(true);
    } catch (caught) {
      setError(isCaptchaError(caught) ? CAPTCHA_ERROR_MESSAGE : errorMessageOf(caught));
    } finally {
      setCaptchaToken(undefined);
      setCaptchaAttempt((attempt) => attempt + 1);
    }
  });

  if (sent) {
    return (
      <AuthShell
        title="Email enviado"
        description="Se existir uma conta com esse endereço, o link de recuperação chega em instantes."
        footer={
          <Link to="/login" className="text-gold underline-offset-4 hover:underline">
            Voltar ao login
          </Link>
        }
      >
        <p className="text-14 text-muted">Confira também a caixa de spam.</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Recuperar senha"
      description="Mandamos um link para criar uma senha nova."
      footer={
        <Link to="/login" className="text-gold underline-offset-4 hover:underline">
          Voltar ao login
        </Link>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            className="h-11 bg-input"
            {...form.register('email')}
          />
          <FormError message={form.formState.errors.email?.message} />
        </div>
        <FormError message={error} />

        <Turnstile key={captchaAttempt} onToken={setCaptchaToken} />

        <Button
          type="submit"
          className="h-11"
          disabled={
            form.formState.isSubmitting || (TURNSTILE_ENABLED && captchaToken === undefined)
          }
        >
          {form.formState.isSubmitting ? 'Enviando…' : 'Enviar link'}
        </Button>
      </form>
    </AuthShell>
  );
}

export const Route = createFileRoute('/forgot-password/')({ component: ForgotPassword });
